import { useMemo, useState } from 'react';
import PageHeader from '@/components/base/PageHeader';
import StatusBadge from '@/components/base/StatusBadge';
import Card from '@/components/base/Card';
import EmptyState from '@/components/base/EmptyState';
import RowActions from '@/components/base/RowActions';
import { taskStatuses, taskPriorities } from '@/types';
import { useData } from '@/context/DataContext';
import { formatDate } from '@/lib/format';
import { useApp } from '@/context/AppContext';

type View = 'liste' | 'kanban' | 'takvim';

export default function Tasks() {
  const { openCreate, openEdit, openDelete } = useApp();
  const { tasks, customerName, projectName } = useData();
  const [view, setView] = useState<View>('liste');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tasks.filter((t) => {
      const matchesQuery = !q || t.title.toLowerCase().includes(q) || customerName(t.customerId).toLowerCase().includes(q) || projectName(t.projectId).toLowerCase().includes(q);
      const matchesStatus = !status || t.status === status;
      const matchesPriority = !priority || t.priority === priority;
      return matchesQuery && matchesStatus && matchesPriority;
    });
  }, [query, status, priority, tasks, customerName, projectName]);

  const viewBtn = (v: View, icon: string) => (
    <button type="button" onClick={() => setView(v)} className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded ${view === v ? 'bg-primary-500 text-white' : 'text-foreground-500 hover:text-foreground-900'}`}>
      <i className={icon} />
    </button>
  );

  return (
    <div className="space-y-5 animate-fade-in-up">
      <PageHeader
        title="Görevler"
        description={`${filtered.length} görev listeleniyor`}
        actions={
          <button type="button" onClick={() => openCreate('task')} className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600">
            <i className="ri-add-line text-lg" /> Yeni Görev
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
          <i className="ri-search-line text-base text-foreground-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Görev ara..." className="h-9 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700">
          <option value="">Tüm Durumlar</option>
          {taskStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={priority} onChange={(e) => setPriority(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700">
          <option value="">Tüm Öncelikler</option>
          {taskPriorities.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <div className="ml-auto inline-flex items-center rounded-md border border-background-300 bg-background-50 p-0.5">
          {viewBtn('liste', 'ri-list-check-2')}
          {viewBtn('kanban', 'ri-layout-column-line')}
          {viewBtn('takvim', 'ri-calendar-line')}
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="Görev bulunamadı" description="Filtreleri değiştirerek tekrar deneyin." />
      ) : view === 'liste' ? (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                  <th className="px-5 py-3">Görev</th><th className="px-4 py-3">Müşteri</th><th className="px-4 py-3">Proje</th><th className="px-4 py-3">Sorumlu</th><th className="px-4 py-3">Son Tarih</th><th className="px-4 py-3">Öncelik</th><th className="px-4 py-3">Durum</th><th className="px-4 py-3 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="border-b border-background-100 transition-colors last:border-0 hover:bg-background-100/60">
                    <td className="px-5 py-3.5 font-medium text-foreground-900">{t.title}</td>
                    <td className="px-4 py-3.5 text-foreground-700">{customerName(t.customerId)}</td>
                    <td className="px-4 py-3.5 text-foreground-700">{projectName(t.projectId)}</td>
                    <td className="px-4 py-3.5 text-foreground-700">{t.assignee}</td>
                    <td className="px-4 py-3.5 text-foreground-600">{formatDate(t.dueDate)}</td>
                    <td className="px-4 py-3.5"><StatusBadge status={t.priority} /></td>
                    <td className="px-4 py-3.5"><StatusBadge status={t.status} /></td>
                    <td className="px-4 py-3.5"><RowActions onEdit={() => openEdit('task', t)} onDelete={() => openDelete('task', t.id, t.title)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : view === 'kanban' ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {taskStatuses.map((s) => {
            const columnTasks = filtered.filter((t) => t.status === s);
            return (
              <div key={s} className="rounded-lg border border-background-200 bg-background-100/40 p-3">
                <div className="flex items-center justify-between px-1 pb-2">
                  <p className="text-sm font-semibold text-foreground-800">{s}</p>
                  <span className="rounded-full bg-background-200 px-2 py-0.5 text-xs font-semibold text-foreground-600">{columnTasks.length}</span>
                </div>
                <div className="space-y-2">
                  {columnTasks.map((t) => (
                    <Card key={t.id} className="p-3">
                      <p className="text-sm font-semibold text-foreground-900">{t.title}</p>
                      <p className="mt-1 text-xs text-foreground-500">{projectName(t.projectId)}</p>
                      <div className="mt-3 flex items-center justify-between">
                        <StatusBadge status={t.priority} />
                        <span className="text-xs text-foreground-500">{formatDate(t.dueDate)}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-foreground-500">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-[10px] font-bold text-primary-700">{t.assignee.slice(0, 1)}</span>
                        {t.assignee}
                      </div>
                      <div className="mt-1 flex justify-end">
                        <RowActions onEdit={() => openEdit('task', t)} onDelete={() => openDelete('task', t.id, t.title)} />
                      </div>
                    </Card>
                  ))}
                  {columnTasks.length === 0 && <p className="rounded-md border border-dashed border-background-300 px-3 py-6 text-center text-xs text-foreground-400">Boş</p>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-4">
          {Array.from(new Set(filtered.map((t) => t.dueDate))).sort().map((date) => (
            <div key={date} className="rounded-lg border border-background-200 bg-background-50">
              <div className="border-b border-background-200 px-5 py-3 text-sm font-semibold text-foreground-900">{formatDate(date)}</div>
              <div className="divide-y divide-background-100">
                {filtered.filter((t) => t.dueDate === date).map((t) => (
                  <div key={t.id} className="flex items-center gap-3 px-5 py-3">
                    <StatusBadge status={t.status} />
                    <span className="min-w-0 flex-1 text-sm font-medium text-foreground-900">{t.title}</span>
                    <span className="text-xs text-foreground-500">{t.assignee}</span>
                    <StatusBadge status={t.priority} />
                    <RowActions onEdit={() => openEdit('task', t)} onDelete={() => openDelete('task', t.id, t.title)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}